import SimpleNotification from "components/Notification/SimpleNotification";
import { toast } from "react-toastify";
import { isUiWebsocketDown } from "utils/NotificationManager/connectionState";

// Number of toasts currently displayed, as last reported by react-toastify's
// onChange callback. NOTE: this total INCLUDES any "Close all" helper toast
// that is displayed.
let activeToastCount = 0;
// Id of the current "Close all" helper toast, else null.
let dismissAllToastId = null;
// Every "Close all" helper toast created and not yet unmounted. react-toastify
// v7 delivers events asynchronously and calls onClose only after the exit
// animation, so an old helper can still be on screen (or still closing) while
// a newer one exists. Tracking all of them lets us count them correctly and
// dismiss strays, so a helper never outlives the content toasts.
const helperToastIds = new Set();
// Re-entrancy guard: creating the "Close all" toast can itself trigger
// onChange; without this we could spawn it more than once.
let creatingDismissAllToast = false;

const isHelperActive = (id) => typeof toast.isActive !== "function" || toast.isActive(id);

const commonOptions = {
    draggable: false,
};

// see https://fkhadra.github.io/react-toastify/introduction
export default class NotificationService {
    static info(content, options) {
        return toast.info(content, { ...commonOptions, ...options });
    }

    static success(content, options) {
        return toast.success(content, { ...commonOptions, ...options });
    }

    static warn(content, options) {
        return toast.warn(content, {
            autoClose: 15000,
            ...commonOptions,
            ...options,
        });
    }

    static error(content, options) {
        return toast.error(content, {
            autoClose: 5000,
            ...commonOptions,
            ...options,
        });
    }

    // Connection-error toast for a *secondary* live-data channel (per-record
    // updates, resource-stats widgets, dependency visualization). While the
    // primary `/ui` websocket is down, the top "Connection to backend lost"
    // banner is already the single persistent signal for the outage, so these
    // secondary sockets' reconnect-storm error events must NOT each raise a
    // fresh toast on top of it (the reported repeating-toast defect). When the
    // primary socket is up, an isolated secondary failure is a genuine problem
    // and the toast still fires. Returns null when suppressed.
    static liveDataError(content, options) {
        if (isUiWebsocketDown()) {
            return null;
        }
        return NotificationService.error(content, options);
    }

    static dismiss(id) {
        toast.dismiss(id);
    }

    static dismissAll() {
        toast.dismiss();
        dismissAllToastId = null;
    }

    // Show the "Close all" helper toast only when at least two *content* toasts
    // are visible. Helper toasts never count toward that threshold, otherwise
    // one lingers (or appears) while only a single real toast remains.
    static checkDismissAllToast() {
        const activeHelpers = [...helperToastIds].filter(isHelperActive);
        const contentToastCount = activeToastCount - activeHelpers.length;

        // Dismiss any helper that is not the current one (left over from an
        // earlier show/dismiss cycle).
        activeHelpers
            .filter((id) => id !== dismissAllToastId)
            .forEach((id) => NotificationService.dismiss(id));

        if (
            contentToastCount > 1 &&
            !dismissAllToastId &&
            !creatingDismissAllToast
        ) {
            creatingDismissAllToast = true;
            const id = toast.info(
                <SimpleNotification title="Close all toast messages" />,
                {
                    ...commonOptions,
                    autoClose: false,
                    closeButton: (
                        <button
                            className="button button--white button--small"
                            onClick={() => {
                                NotificationService.dismissAll();
                            }}
                        >
                            Close all
                        </button>
                    ),
                    className: "close-all-toasts",
                    // onClose runs after the exit animation; by then a newer
                    // helper may be current, so only forget our own id.
                    onClose: () => {
                        helperToastIds.delete(id);
                        if (dismissAllToastId === id) {
                            dismissAllToastId = null;
                        }
                    },
                }
            );
            helperToastIds.add(id);
            dismissAllToastId = id;
            creatingDismissAllToast = false;
        } else if (contentToastCount <= 1 && dismissAllToastId) {
            NotificationService.dismiss(dismissAllToastId);
            dismissAllToastId = null;
        }
    }
}

toast.onChange((count) => {
    activeToastCount = count;
    NotificationService.checkDismissAllToast();
});
